import React, { useCallback, useEffect, useState } from 'react';
import './AlphaScanner.css';
import { getAlphaScanner } from '../api/brapi';
import { formatTradingDate } from '../utils/dates';
import { motivoAlpha, candidatasAlpha, baixarCsvCandidatas, percentualAlpha as percentual } from '../utils/csvCandidatasAlpha';
import AlphaGraficoPainel from './AlphaGraficoPainel';

const sim = valor => (valor ? 'Sim' : 'Não');
const numero = (valor, sufixo = '') => (Number.isFinite(Number(valor)) && valor != null
  ? `${Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${sufixo}`
  : '—');

// FAT-45: o estado vem do backend (`linha.estado`); aqui só a cor.
const CLASSE_ESTADO = {
  Candidata: 'as-estado-candidata',
  'Entrada sinalizada': 'as-estado-entrada',
  'Em posição': 'as-estado-posicao',
  'Saída sinalizada': 'as-estado-saida',
};
const NOTA_RANKING = 'Ranking entre as ações que passaram pelos filtros; o menor RSI não fica automaticamente em 1º';

function ResumoMercado({ dados }) {
  const mercado = dados?.mercado;
  const p = dados?.parametros;
  if (!mercado) return <h2 data-testid="alpha-cabecalho">Alpha Brasil</h2>;
  return (
    <>
      <h2 data-testid="alpha-cabecalho">
        Alpha Brasil · <span className={mercado.favoravel ? 'as-favoravel' : 'as-desfavoravel'}>
          {mercado.favoravel ? 'Mercado favorável' : 'Mercado desfavorável'}
        </span>
      </h2>
      <p className="as-resumo" data-testid="alpha-resumo-mercado">
        IBOV em {p?.janelaMercadoPregoes ?? 126} pregões: <strong data-testid="alpha-retorno-ibov">{percentual(mercado.retornoPct)}</strong>
        {' · '}Última avaliação: {formatTradingDate(dados.avaliacao?.ultimaAvaliacao)} (fechamento da semana)
        {' · '}Próxima avaliação: {formatTradingDate(dados.avaliacao?.proximaAvaliacao)}
      </p>
      {!mercado.favoravel && (
        <div className="as-faixa" role="status" data-testid="alpha-faixa-mercado">
          <strong>Novas entradas bloqueadas pelo mercado.</strong> Isso NÃO significa vender as posições abertas: as saídas continuam
          pelas regras de saída (RSI semanal acima de {p?.limiarRsiSaida ?? 80} ou fechamento mais de {p?.quedaMaximaPct ?? 10}% abaixo
          da entrada).
        </div>
      )}
    </>
  );
}

// FAT-46: regra e base do universo vêm do backend (alphaUniverso.json + cadastro de ativos), nada escrito à mão.
function RegraUniverso({ universo }) {
  if (!universo) return null;
  const tipos = (universo.tiposExcluidos || []).map(t => t.toUpperCase()).join(' e ');
  return (
    <p className="as-nota" data-testid="alpha-universo">
      Universo: {universo.tamanho} ações de maior volume financeiro médio em {universo.janelaPregoes} pregões, revisto no 1º pregão do mês.
      {' '}Parte dos {universo.ativosAcompanhados} ativos acompanhados pelo FatCat (não da B3 inteira)
      {tipos && `; ${tipos} ficam fora pelo tipo do ativo`}.
      {universo.excluidosPorTipo?.length > 0 && ` Fora por tipo: ${universo.excluidosPorTipo.join(', ')}.`}
    </p>
  );
}

export default function AlphaScanner() {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  // FAT-44: candidata com o painel do gráfico aberto (a tabela continua montada: filtros e scroll ficam).
  const [aberta, setAberta] = useState(null);
  const fechar = useCallback(() => setAberta(null), []);

  useEffect(() => {
    let ativo = true;
    getAlphaScanner()
      .then(resultado => { if (ativo) setDados(resultado); })
      .catch(err => { if (ativo) setErro(err?.response?.data?.error || err.message || 'Falha ao carregar o scanner Alpha.'); })
      .finally(() => { if (ativo) setCarregando(false); });
    return () => { ativo = false; };
  }, []);

  const linhas = dados?.linhas || [];
  // FAT-45: lista principal só com oportunidades (linha com estado); o resto fica recolhido.
  const oportunidades = linhas.filter(l => l.estado);
  const fora = linhas.filter(l => !l.estado);
  const temCandidatas = !erro && !carregando && !!dados?.data && candidatasAlpha(dados).length > 0;

  const linhaTabela = (l, comEstado) => {
    const candidata = l.situacao === 'Candidata';
    return (
      <tr key={l.ativo} data-testid={`alpha-linha-${l.ativo}`} className={candidata ? 'as-clicavel' : undefined}
        onClick={candidata ? () => setAberta(l) : undefined}>
        <td>
          {candidata ? (
            <button type="button" className="as-ticker" data-testid={`alpha-ticker-${l.ativo}`} title="Ver gráfico semanal"
              onClick={e => { e.stopPropagation(); setAberta(l); }}>{l.ativo}</button>
          ) : l.ativo}
        </td>
        <td>{sim(l.liquidezElegivel)}</td>
        <td>{numero(l.rsiSemanal)}</td>
        <td>{numero(l.volatilidade, '%')}</td>
        <td>{l.ranking != null ? `${l.ranking}ª` : '—'}</td>
        <td data-testid={`alpha-situacao-${l.ativo}`}>
          {comEstado ? <span className={`as-estado ${CLASSE_ESTADO[l.estado] || ''}`}>{l.estado}</span> : l.situacao}
        </td>
        <td>{motivoAlpha(l, dados.parametros)}</td>
      </tr>
    );
  };
  const cabecalho = (
    <thead>
      <tr>
        <th>Ativo</th><th>Liquidez elegível?</th><th>RSI semanal</th><th>Volatilidade 100 dias</th><th>Ranking</th><th>Situação</th><th>Motivo</th>
      </tr>
    </thead>
  );

  return (
    <section data-testid="alpha-scanner" className="as-secao">
      <ResumoMercado dados={dados} />
      <p className="as-nota" data-testid="alpha-scanner-nota">
        Candidata ainda depende de seleção, vagas e capital. O FatCat não executa ordens.
      </p>
      {dados?.data && (
        <p className="as-nota" data-testid="alpha-scanner-parametros">
          Referência: {formatTradingDate(dados.data)}
          {dados.parametros && ` · RSI(${dados.parametros.periodoRsi}) semanal < ${dados.parametros.limiteRsiSemanal} · volatilidade anualizada de ${dados.parametros.janelaVolatilidadePregoes} pregões · até ${dados.parametros.maxPosicoes} posições`}
        </p>
      )}
      <RegraUniverso universo={dados?.universo} />
      {carregando && <p data-testid="alpha-scanner-carregando">Carregando…</p>}
      {erro && <p role="alert" data-testid="alpha-scanner-erro">{erro}</p>}
      {!carregando && !erro && !linhas.length && <p data-testid="alpha-scanner-vazio">Sem dados para o scanner Alpha.</p>}
      {!carregando && !erro && linhas.length > 0 && (
        <p className="as-acoes">
          <button type="button" className="as-btn" data-testid="alpha-exportar-csv" disabled={!temCandidatas} onClick={() => baixarCsvCandidatas(dados)}>
            Exportar CSV
          </button>
          {!temCandidatas && <span role="status" data-testid="alpha-sem-candidatas"> Não há candidatas nesta semana.</span>}
          {temCandidatas && <span className="as-nota"> Clique numa candidata para ver o gráfico semanal.</span>}
        </p>
      )}
      {linhas.length > 0 && (
        <>
          {oportunidades.length > 0 ? (
            <div className="as-tabela-wrap"><table className="as-tabela" data-testid="alpha-scanner-tabela">
              {cabecalho}
              <tbody>{oportunidades.map(l => linhaTabela(l, true))}</tbody>
            </table></div>
          ) : (
            <p data-testid="alpha-sem-oportunidades">Nenhuma oportunidade nesta avaliação (sem candidatas nem posições).</p>
          )}
          <p className="as-nota as-rodape" data-testid="alpha-nota-ranking">{NOTA_RANKING}</p>
          {fora.length > 0 && (
            <details className="as-fora" data-testid="alpha-fora-filtros">
              <summary>Não passaram nos filtros ({fora.length})</summary>
              <div className="as-tabela-wrap"><table className="as-tabela" data-testid="alpha-fora-tabela">
                {cabecalho}
                <tbody>{fora.map(l => linhaTabela(l, false))}</tbody>
              </table></div>
            </details>
          )}
        </>
      )}
      {aberta && <AlphaGraficoPainel linha={aberta} parametros={dados?.parametros} onFechar={fechar} />}
    </section>
  );
}
